import type { GameState } from '@pacmates/engine';
import { COLORS } from './colors';
import { renderBackground } from './renderBackground';

// ---------------------------------------------------------------------------
// Continuous maze wall contours (plan-gen1.md Experiment 1)
//
// The old renderer drew every '#' cell as an INDEPENDENT padded roundRect +
// stroke, so a straight run of wall cells rendered as a row of separate
// pills -- the maze read as a checkerboard of boxes, not a corridor. This
// replaces that with edge-stroking: for each wall cell, only the edges that
// border open space are stroked, and adjacent cells' edges are computed so
// collinear runs abut end-to-end into one continuous piped outline, with
// convex arcs at true outer corners and extended (concave) joins at inner
// notches. Ported from the fixture-proven reference at
// .../scratchpad/contour-proof.mjs per plan-gen1.md -- not re-derived.
// ---------------------------------------------------------------------------

export type ContourOp =
  | { kind: 'seg'; x1: number; y1: number; x2: number; y2: number }
  | { kind: 'arc'; cx: number; cy: number; startAngle: number; endAngle: number; r: number };

type WallGridLike = readonly string[][] | readonly string[];

function cellAt(wallGrid: WallGridLike, cols: number, rows: number, x: number, y: number): string {
  if (y < 0 || y >= rows || x < 0 || x >= cols) return ' ';
  const row = wallGrid[y]!;
  return typeof row === 'string' ? row[x]! : row[x]!;
}

function isOpen(wallGrid: WallGridLike, cols: number, rows: number, x: number, y: number): boolean {
  if (y < 0 || y >= rows || x < 0 || x >= cols) return true;
  return cellAt(wallGrid, cols, rows, x, y) !== '#';
}

/** Pure, canvas-free: emits the stroke geometry for the whole maze's wall
 * contour as a flat op list. Exported for unit testing without a DOM/canvas
 * and reused by buildMazePaths() below to build the real Path2D. */
export function wallContourOps(
  wallGrid: WallGridLike,
  cols: number,
  rows: number,
  tile: number,
  pad: number,
  radius: number,
): ContourOp[] {
  const t = tile;
  const p = pad;
  const r = radius;
  const out: ContourOp[] = [];

  const open = (x: number, y: number) => isOpen(wallGrid, cols, rows, x, y);

  // endpoint rule shared by all four edges:
  //   orthogonal neighbor open -> stop short by r (convex corner, arc emitted separately)
  //   orthogonal neighbor wall, diagonal open -> stop exactly at the cell bound (abuts neighbor's collinear edge)
  //   orthogonal neighbor wall, diagonal wall -> extend PAST the bound by p (concave inner corner)
  const end = (orthOpen: boolean, diagOpen: boolean, bound: number, shortVal: number, sign: 1 | -1): number =>
    orthOpen ? shortVal : diagOpen ? bound : bound - sign * p;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (cellAt(wallGrid, cols, rows, x, y) !== '#') continue;

      const U = open(x, y - 1);
      const D = open(x, y + 1);
      const L = open(x - 1, y);
      const R = open(x + 1, y);
      const UL = open(x - 1, y - 1);
      const UR = open(x + 1, y - 1);
      const DL = open(x - 1, y + 1);
      const DR = open(x + 1, y + 1);

      const X0 = x * t;
      const X1 = x * t + t;
      const Y0 = y * t;
      const Y1 = y * t + t;
      const x0 = X0 + p;
      const x1 = X1 - p;
      const y0 = Y0 + p;
      const y1 = Y1 - p;

      if (U) {
        const xa = end(L, UL, X0, x0 + r, 1);
        const xb = end(R, UR, X1, x1 - r, -1);
        out.push({ kind: 'seg', x1: xa, y1: y0, x2: xb, y2: y0 });
      }
      if (D) {
        const xa = end(L, DL, X0, x0 + r, 1);
        const xb = end(R, DR, X1, x1 - r, -1);
        out.push({ kind: 'seg', x1: xa, y1: y1, x2: xb, y2: y1 });
      }
      if (L) {
        const ya = end(U, UL, Y0, y0 + r, 1);
        const yb = end(D, DL, Y1, y1 - r, -1);
        out.push({ kind: 'seg', x1: x0, y1: ya, x2: x0, y2: yb });
      }
      if (R) {
        const ya = end(U, UR, Y0, y0 + r, 1);
        const yb = end(D, DR, Y1, y1 - r, -1);
        out.push({ kind: 'seg', x1: x1, y1: ya, x2: x1, y2: yb });
      }

      // convex arcs: two adjacent sides both exposed -> round the outer corner.
      // arcTo-equivalent 180deg->270deg style quarter circles, angles in radians.
      const TAU = Math.PI * 2;
      if (U && L) out.push({ kind: 'arc', cx: x0 + r, cy: y0 + r, startAngle: Math.PI, endAngle: (3 * Math.PI) / 2, r });
      if (U && R) out.push({ kind: 'arc', cx: x1 - r, cy: y0 + r, startAngle: (3 * Math.PI) / 2, endAngle: TAU, r });
      if (D && L) out.push({ kind: 'arc', cx: x0 + r, cy: y1 - r, startAngle: Math.PI / 2, endAngle: Math.PI, r });
      if (D && R) out.push({ kind: 'arc', cx: x1 - r, cy: y1 - r, startAngle: 0, endAngle: Math.PI / 2, r });
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Op-list -> Path2D command translation, extracted as a pure function so the
// unit tests can assert on the exact command stream fed to Path2D without a
// DOM/canvas. buildMazePaths() below replays these commands onto a real
// Path2D -- this is the ONLY place that turns ContourOp[] into path commands,
// so there is exactly one source of truth between the tests and the render.
// ---------------------------------------------------------------------------

export type PathCmd =
  | ['M', number, number]
  | ['L', number, number]
  | ['A', number, number, number, number, number];

export function contourPathCommands(ops: ContourOp[]): PathCmd[] {
  const cmds: PathCmd[] = [];
  for (const op of ops) {
    if (op.kind === 'seg') {
      cmds.push(['M', op.x1, op.y1]);
      cmds.push(['L', op.x2, op.y2]);
    } else {
      // Every arc must start its OWN subpath: moveTo the arc's start point
      // first. Without this, Path2D.arc() on an open subpath (current point
      // left at the end of the previous seg/arc) emits an implicit lineTo
      // from that stale current point to the arc's start -- a stray chord
      // cutting across the corner. The geometry already lines up so this
      // moveTo lands exactly on the adjoining segment's endpoint (tangent),
      // and lineCap='round' hides the resulting subpath break.
      const startX = op.cx + op.r * Math.cos(op.startAngle);
      const startY = op.cy + op.r * Math.sin(op.startAngle);
      cmds.push(['M', startX, startY]);
      cmds.push(['A', op.cx, op.cy, op.r, op.startAngle, op.endAngle]);
    }
  }
  return cmds;
}

function replayPathCommands(path: Path2D, cmds: PathCmd[]): void {
  for (const cmd of cmds) {
    if (cmd[0] === 'M') path.moveTo(cmd[1], cmd[2]);
    else if (cmd[0] === 'L') path.lineTo(cmd[1], cmd[2]);
    else path.arc(cmd[1], cmd[2], cmd[3], cmd[4], cmd[5]);
  }
}

/** Builds the stroke Path2D (rim outline) and fill Path2D (wall interior,
 * inset only on exposed sides so adjacent fills abut seamlessly) for the
 * whole static maze. Called once per (wallGrid, tile) and cached. */
function buildMazePaths(
  wallGrid: WallGridLike,
  cols: number,
  rows: number,
  tile: number,
  pad: number,
  radius: number,
): { stroke: Path2D; fill: Path2D } {
  const ops = wallContourOps(wallGrid, cols, rows, tile, pad, radius);
  const stroke = new Path2D();
  replayPathCommands(stroke, contourPathCommands(ops));

  // Fill: per wall cell, a rect inset by pad on exposed sides only, extended
  // to the full cell bound on wall sides -- adjacent cells' fills then abut
  // with no seam and no gap, matching the stroked contour.
  const fill = new Path2D();
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (cellAt(wallGrid, cols, rows, x, y) !== '#') continue;
      const U = isOpen(wallGrid, cols, rows, x, y - 1);
      const D = isOpen(wallGrid, cols, rows, x, y + 1);
      const L = isOpen(wallGrid, cols, rows, x - 1, y);
      const R = isOpen(wallGrid, cols, rows, x + 1, y);
      const X0 = x * tile;
      const X1 = x * tile + tile;
      const Y0 = y * tile;
      const Y1 = y * tile + tile;
      const left = L ? X0 + pad : X0;
      const rightB = R ? X1 - pad : X1;
      const top = U ? Y0 + pad : Y0;
      const bottom = D ? Y1 - pad : Y1;
      fill.rect(left, top, rightB - left, bottom - top);
    }
  }

  return { stroke, fill };
}

interface MazePathCache {
  key: string;
  tile: number;
  stroke: Path2D;
  fill: Path2D;
}

let mazeCache: MazePathCache | null = null;

function gridKey(wallGrid: WallGridLike): string {
  // Wall grid is static per game but arrives as a freshly-parsed JSON array
  // every snapshot (new reference each time), so cache on CONTENT not
  // reference identity -- join is O(rows), far cheaper than rebuilding the
  // whole Path2D geometry every rAF at 60fps.
  return typeof wallGrid[0] === 'string' ? (wallGrid as readonly string[]).join('\n') : wallGrid.map((r) => (r as string[]).join('')).join('\n');
}

function getMazePaths(wallGrid: WallGridLike, cols: number, rows: number, tile: number, pad: number, radius: number): { stroke: Path2D; fill: Path2D } {
  const key = gridKey(wallGrid);
  if (mazeCache && mazeCache.key === key && mazeCache.tile === tile) {
    return { stroke: mazeCache.stroke, fill: mazeCache.fill };
  }
  const built = buildMazePaths(wallGrid, cols, rows, tile, pad, radius);
  mazeCache = { key, tile, ...built };
  return built;
}

// ---------------------------------------------------------------------------
// Frame render
// ---------------------------------------------------------------------------

const WALL_PAD_FRAC = 0.18;
const WALL_RADIUS_FRAC = 0.2;

/** Lightens a hex color by blending it toward white by `amount` (0..1). Used
 * only for the wallFill depth gradient -- never applied to a tolerance-6
 * asserted core color. */
function lighten(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

/** Draws the static wall geometry + current dot/energizer grids. Pure
 * render -- reads directly off the authoritative snapshot's wallGrid /
 * pelletGrid / energizerGrid, never a client-maintained copy. */
export function renderMaze(ctx: CanvasRenderingContext2D, state: GameState, tile: number, nowMs: number): void {
  const { wallGrid, pelletGrid, energizerGrid, cols, rows } = state;

  renderBackground(ctx, cols * tile, rows * tile);

  const pad = tile * WALL_PAD_FRAC;
  const radius = Math.min(tile * WALL_RADIUS_FRAC, (tile - 2 * pad) / 2);
  const { stroke, fill } = getMazePaths(wallGrid, cols, rows, tile, pad, radius);

  // Fill wall interior first (seamless, no gaps between adjacent cells).
  // Subtle vertical gradient (wallFill -> slightly lighter -> wallFill) for
  // depth -- COLORS.wallFill itself is unchanged and this hex is never a
  // tolerance-6 pixel-assertion target (only wallRim/pellet/energizer/p1/
  // fright/frightFlash are), so the gradient is safe.
  const wallGradient = ctx.createLinearGradient(0, 0, 0, rows * tile);
  wallGradient.addColorStop(0, COLORS.wallFill);
  wallGradient.addColorStop(0.5, lighten(COLORS.wallFill, 0.12));
  wallGradient.addColorStop(1, COLORS.wallFill);
  ctx.fillStyle = wallGradient;
  ctx.fill(fill);

  // Neon rim: glow pass BEHIND (additive bloom), then an opaque solid-core
  // pass ON TOP at exactly COLORS.wallRim with shadowBlur=0 -- nothing may
  // composite over the core pixel, so shoot.mjs's tolerance-6 exact-color
  // sampling still lands on the true wallRim hex (plan-gen1.md Experiment 2).
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const lineWidth = Math.max(1.5, tile * 0.08);
  ctx.lineWidth = lineWidth;

  ctx.save();
  ctx.shadowColor = COLORS.wallRim;
  ctx.shadowBlur = tile * 0.35;
  ctx.strokeStyle = COLORS.wallRim;
  ctx.stroke(stroke);
  ctx.restore();

  ctx.shadowBlur = 0;
  ctx.strokeStyle = COLORS.wallRim;
  ctx.stroke(stroke);

  // Ghost-house door -- unchanged from prior rendering.
  for (let y = 0; y < rows; y++) {
    const row = wallGrid[y]!;
    for (let x = 0; x < cols; x++) {
      if (row[x] === '-') {
        ctx.fillStyle = COLORS.door;
        ctx.fillRect(x * tile + tile * 0.1, y * tile + tile * 0.4, tile * 0.8, tile * 0.2);
      }
    }
  }

  // Pellets + energizers: per-frame (they mutate), shadowBlur set ONCE per
  // layer rather than per entity inside the loop (perf: hoisted state changes).
  ctx.shadowColor = COLORS.pellet;
  ctx.shadowBlur = tile * 0.25;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (!pelletGrid[y]![x]) continue;
      const cx = x * tile + tile / 2;
      const cy = y * tile + tile / 2;
      ctx.fillStyle = COLORS.pellet;
      ctx.beginPath();
      ctx.arc(cx, cy, tile * 0.08, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.shadowBlur = 0;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (!energizerGrid[y]![x]) continue;
      const cx = x * tile + tile / 2;
      const cy = y * tile + tile / 2;
      // DEFECT 21 fix: the old code pulsed the WHOLE pellet's globalAlpha
      // in [0.5, 1.0] straight over the dark background -- at its dimmest
      // (alpha 0.5) the composited pixel measured ~(133,135,150) on a real
      // canvas, LOWER luminance than the plain tan dot COLORS.pellet
      // (255,220,180) it's supposed to visually outrank, so a "power
      // pellet" read as duller than a normal dot for roughly half of every
      // pulse cycle (BUGLEDGER.md #21). Fix: split into a translucent
      // OUTER GLOW (this is what pulses, giving the "breathing" animation)
      // and a SOLID INNER CORE that is ALWAYS drawn at full opacity in
      // COLORS.energizer -- the sampled center pixel is therefore the
      // literal white (255,255,255) at every phase, unconditionally
      // brighter/whiter than the pellet and P1 colors, not just above a
      // floor (colors.test.ts DEFECT 21 block asserts this directly; the
      // shoot.mjs harness samples both the dim AND bright phase of a real
      // energizer and asserts the measured dim-phase colour too).
      const glowAlpha = energizerGlowAlpha(nowMs, x, y);
      ctx.fillStyle = COLORS.energizer;
      ctx.globalAlpha = glowAlpha;
      ctx.beginPath();
      // Experiment 2: widen the pulsing outer glow only (radius multipliers
      // 0.38/0.16, up from the original 0.3/0.1) -- the solid inner core
      // below (radius 0.22, alpha 1, COLORS.energizer) is UNTOUCHED, exactly
      // per plan-gen1.md ("keep the existing solid-white-core logic EXACTLY;
      // only widen the glow").
      ctx.arc(cx, cy, tile * (0.38 + 0.16 * ((glowAlpha - ENERGIZER_GLOW_ALPHA_MIN) / (ENERGIZER_GLOW_ALPHA_MAX - ENERGIZER_GLOW_ALPHA_MIN))), 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, tile * 0.22, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

// DEFECT 21: the glow's alpha range -- exported so both the render loop
// above and the unit-test regression (colors.test.ts) compute the exact
// same numbers, instead of the test hand-copying a magic range that could
// silently drift from the real render.
export const ENERGIZER_GLOW_ALPHA_MIN = 0.25;
export const ENERGIZER_GLOW_ALPHA_MAX = 0.6;

export function energizerGlowAlpha(nowMs: number, x: number, y: number): number {
  const phase = 0.5 + 0.5 * Math.sin(nowMs / 180 + x + y); // 0..1
  return ENERGIZER_GLOW_ALPHA_MIN + (ENERGIZER_GLOW_ALPHA_MAX - ENERGIZER_GLOW_ALPHA_MIN) * phase;
}

export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
